import React, { useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { isAdmin, isSuperAdmin } from '../utils/permissions';

/**
 * Higher-Order Component that adds account selection awareness to a component
 *
 * This HOC will:
 * 1. Prevent API calls when no account is selected for admins with multiple accounts
 * 2. Handle loading states when waiting for account selection
 * 3. Provide account selection props to the wrapped component
 *
 * @param {React.Component} WrappedComponent - The component to wrap
 * @returns {React.Component} - The wrapped component with account selection awareness
 */
const withAccountSelection = (WrappedComponent) => {
  const WithAccountSelection = (props) => {
    const {
      selectedAccountId,
      accounts,
      isAccountSelectionRequired,
      user,
      getAccountIdToUse
    } = useAccount();

    const [isReady, setIsReady] = useState(false);

    // Determine if the component is ready to make API calls
    useEffect(() => {
      // Use either the user from context or from props
      const effectiveUser = user || props.user;

      // If the user is not loaded yet, we're not ready
      if (!effectiveUser) {
        setIsReady(false);
        return;
      }

      // If account selection is required but not done, we're not ready
      if (isAccountSelectionRequired()) {
        setIsReady(false);
        return;
      }

      // Get the account ID to use
      const accountId = getAccountIdToUse();

      // If we're a super admin or regular user, we need a valid accountId
      if (!isAdmin(effectiveUser) || isSuperAdmin(effectiveUser)) {
        if (!accountId) {
          setIsReady(false);
          return;
        }
      }

      // Otherwise, we're ready
      setIsReady(true);
    }, [user, props.user, isAccountSelectionRequired, selectedAccountId, getAccountIdToUse, isAdmin, isSuperAdmin]);

    // Use either the user from context or from props
    const effectiveUser = user || props.user;

    // Get the accountId to use based on user role
    const accountId = getAccountIdToUse();

    // If account selection is required but not done, don't show any placeholder
    // Just pass isReady=false to the wrapped component
    if (isAccountSelectionRequired()) {
      return (
        <WrappedComponent
          {...props}
          isReady={false}
          accountId={accountId}
          selectedAccountId={selectedAccountId}
          accounts={accounts}
          user={effectiveUser}
        />
      );
    }

    // Pass all props plus account-related props to the wrapped component
    return (
      <WrappedComponent
        {...props}
        isReady={isReady}
        accountId={accountId}
        selectedAccountId={selectedAccountId}
        accounts={accounts}
        user={effectiveUser}
      />
    );
  };

  // Set display name for debugging
  WithAccountSelection.displayName = `WithAccountSelection(${getDisplayName(WrappedComponent)})`;

  return WithAccountSelection;
};

// Helper function to get the display name of a component
function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default withAccountSelection;
